import {
  DEFAULT_MARKDOWN_PLACEHOLDER,
  DEFAULT_COLOR_PRIMARY,
} from '../constants'
import { basePropertiesSchema } from '../base-schema-properties'
import { buttonsPropertiesSchema, textFontFamilySchema } from '../common'

const responsiveProperties = (
  prefix: 'mobile' | 'medium',
  viewName: string,
  toggleTitle: string
) => {
  const enabledProperty = `override${
    prefix.charAt(0).toUpperCase() + prefix.slice(1)
  }View`
  const hidden = `{{ parentFormData.${enabledProperty} !== true }}`
  const bgImageProperty = `${prefix}BgImage`

  return {
    [enabledProperty]: {
      title: toggleTitle,
      type: 'boolean',
      default: false,
    },
    [`${prefix}BgImage`]: {
      title: `Background image — ${viewName}`,
      description: 'Leave empty to use the desktop background.',
      type: 'string',
      'ui:hidden': hidden,
    },
    [`${prefix}BgColor`]: {
      title: `Background color — ${viewName}`,
      description:
        'Choose a color to replace the desktop background image for this view.',
      type: 'string',
      'ui:hidden': `{{ parentFormData.${enabledProperty} !== true || !!parentFormData.${bgImageProperty} }}`,
    },
    [`${prefix}ImageRatioMode`]: {
      title: `Use image ratio — ${viewName}`,
      type: 'string',
      enum: ['desktop', 'enabled', 'disabled'],
      enumNames: [
        'Same as desktop',
        'Use image ratio',
        "Don't use image ratio",
      ],
      default: 'desktop',
      'ui:widget': 'RadioWidget',
      'ui:hidden': hidden,
    },
    [`${prefix}PaddingSize`]: {
      title: `Paddings — ${viewName}`,
      enum: ['xs', 'sm', 'md', 'lg'],
      enumNames: ['Extra small', 'Small', 'Medium', 'Large'],
      default: 'md',
      'ui:widget': 'RadioWidget',
      'ui:hidden': `{{ parentFormData.${enabledProperty} !== true || parentFormData.useImageRatio !== true || parentFormData.${prefix}ImageRatioMode !== "disabled" }}`,
    },
    [`${prefix}LogoWidth`]: {
      title: `Logo width — ${viewName} (in px)`,
      description: 'Uses the desktop width until changed.',
      type: 'number',
      'ui:hidden': hidden,
    },
    [`${prefix}LogoAlign`]: {
      title: `Logo horizontal alignment — ${viewName}`,
      description: 'Uses the desktop alignment until changed.',
      enum: ['left', 'center', 'right'],
      enumNames: ['Left', 'Center', 'Right'],
      'ui:widget': 'RadioWidget',
      'ui:hidden': hidden,
    },
    [`${prefix}MaxWidth`]: {
      title: `Max width of the content — ${viewName} (in px)`,
      description: 'Uses the desktop width until changed.',
      type: 'number',
      'ui:hidden': hidden,
    },
    [`${prefix}TextAlign`]: {
      title: `Text Align — ${viewName}`,
      description: 'Uses the desktop alignment until changed.',
      enum: ['left', 'center', 'right'],
      enumNames: ['Left', 'Center', 'Right'],
      'ui:widget': 'RadioWidget',
      'ui:hidden': hidden,
    },
  }
}

export default {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'Cover',
  description: 'Beautiful block with background, text and buttons',
  type: 'object',
  required: [],
  properties: {
    ...basePropertiesSchema,
    logo: {
      title: 'Logo or other svg/png image before text',
      type: 'string',
      default: '',
    },
    logoWidth: {
      title: 'Logo width (in px)',
      description: "If it's not set, the logo width will be 240px",
      type: 'number',
    },
    logoAlign: {
      title: 'Logo horizontal alignment',
      enum: ['left', 'center', 'right'],
      enumNames: ['Left', 'Center', 'Right'],
      default: 'left',
      'ui:widget': 'RadioWidget',
    },
    text: {
      title: 'Text',
      type: 'string',
      'ui:options': {
        placeholder: DEFAULT_MARKDOWN_PLACEHOLDER,
        type: 'textarea',
        rows: 6,
      },
    },
    textColor: {
      title: 'Text Color',
      type: 'string',
      default: '#ffffff',
    },
    ...textFontFamilySchema,
    linkColor: {
      title: 'Link Color',
      type: 'string',
      default: DEFAULT_COLOR_PRIMARY,
    },
    textAlign: {
      title: 'Text Align',
      enum: ['left', 'center', 'right'],
      enumNames: ['Left', 'Center', 'Right'],
      default: 'center',
      'ui:widget': 'RadioWidget',
    },
    useImageRatio: {
      title: 'Use image ratio',
      type: 'boolean',
      default: false,
      'ui:style':
        '{{ parentFormData.bgImage ? {} : { "pointer-events": "none", opacity: "20%" } }}',
    },
    bgImage: {
      title: 'Background image',
      type: 'string',
      default: '',
    },
    bgColor: {
      title: 'Background color',
      type: 'string',
      'ui:hidden': '{{ !!parentFormData.bgImage }}',
    },
    paddingSize: {
      title: 'Paddings',
      enum: ['xs', 'sm', 'md', 'lg'],
      enumNames: ['Extra small', 'Small', 'Medium', 'Large'],
      default: 'md',
      'ui:widget': 'RadioWidget',
      'ui:hidden':
        '{{ parentFormData.useImageRatio === true && parentFormData.bgImage }}',
    },
    maxWidth: {
      title: 'Max width of the content (in px)',
      description: "If it's not set, the content width will be 576px",
      type: 'number',
    },
    ...responsiveProperties('mobile', 'mobile', 'Override mobile view'),
    ...responsiveProperties(
      'medium',
      'tablets and small laptops',
      'Override tablet and small laptop view'
    ),
    ...buttonsPropertiesSchema,
  },
}
